// Static interface copy in every supported app language. User and model content is not translated here.
import type { NamespaceCatalog } from '@/i18n/ns';

const en = {
  captureLine: '{n} of {total} images available',
  provenance: '{source} · as seen {at}',
  provenanceNoSource: 'As seen {at}',
  changedSince: 'The image has changed since then.',
  observedHeading: 'Image as observed',
  imageCapturing: 'Loading image',
  imageMissing: 'No image',
  imageUnavailable: 'Image no longer available',
  imageRefused: 'Image couldn’t be used',
  imageOpen: 'Open image: {alt}',
  imageInspect: 'Image · {alt}',
  imageSeen: 'seen {date}',
};

export type AppMediaCopyKey = keyof typeof en;

export const appMediaCopy: NamespaceCatalog<keyof typeof en> = {
  en: {
    captureLine: '{n} of {total} images available',
    provenance: '{source} · as seen {at}',
    provenanceNoSource: 'As seen {at}',
    changedSince: 'The image has changed since then.',
    observedHeading: 'Image as observed',
    imageCapturing: 'Loading image',
    imageMissing: 'No image',
    imageUnavailable: 'Image no longer available',
    imageRefused: 'Image couldn’t be used',
    imageOpen: 'Open image: {alt}',
    imageInspect: 'Image · {alt}',
    imageSeen: 'seen {date}',
  },
  bg: {
    captureLine: 'Налични са {n} от {total} изображения',
    provenance: '{source} · видяно на {at}',
    provenanceNoSource: 'Видяно на {at}',
    changedSince: 'Оттогава изображението се е променило.',
    observedHeading: 'Изображение според наблюдението',
    imageCapturing: 'Зареждане на изображението',
    imageMissing: 'Няма изображение',
    imageUnavailable: 'Изображението вече не е налично',
    imageRefused: 'Изображението не може да бъде използвано',
    imageOpen: 'Отваряне на изображението: {alt}',
    imageInspect: 'Изображение · {alt}',
    imageSeen: 'видяно на {date}',
  },
  cs: {
    captureLine: 'K dispozici je {n} z {total} obrázků',
    provenance: '{source} · zobrazeno {at}',
    provenanceNoSource: 'Zobrazeno {at}',
    changedSince: 'Obrázek se od té doby změnil.',
    observedHeading: 'Obrázek při pozorování',
    imageCapturing: 'Načítání obrázku',
    imageMissing: 'Žádný obrázek',
    imageUnavailable: 'Obrázek již není k dispozici',
    imageRefused: 'Obrázek nelze použít',
    imageOpen: 'Otevřít obrázek: {alt}',
    imageInspect: 'Obrázek · {alt}',
    imageSeen: 'zobrazeno {date}',
  },
  da: {
    captureLine: '{n} af {total} tilgængelige billeder',
    provenance: '{source} · set {at}',
    provenanceNoSource: 'Set {at}',
    changedSince: 'Billedet er ændret siden da.',
    observedHeading: 'Billede som observeret',
    imageCapturing: 'Indlæser billede',
    imageMissing: 'Intet billede',
    imageUnavailable: 'Billedet er ikke længere tilgængeligt',
    imageRefused: 'Billedet kunne ikke bruges',
    imageOpen: 'Åbn billede: {alt}',
    imageInspect: 'Billede · {alt}',
    imageSeen: 'set {date}',
  },
  de: {
    captureLine: '{n} von {total} Bildern verfügbar',
    provenance: '{source} · gesehen am {at}',
    provenanceNoSource: 'Gesehen am {at}',
    changedSince: 'Das Bild wurde seitdem geändert.',
    observedHeading: 'Bild wie beobachtet',
    imageCapturing: 'Bild wird geladen',
    imageMissing: 'Kein Bild',
    imageUnavailable: 'Bild nicht mehr verfügbar',
    imageRefused: 'Bild konnte nicht verwendet werden',
    imageOpen: 'Bild öffnen: {alt}',
    imageInspect: 'Bild · {alt}',
    imageSeen: 'gesehen am {date}',
  },
  el: {
    captureLine: '{n} από {total} διαθέσιμες εικόνες',
    provenance: '{source} · όπως εμφανίστηκε {at}',
    provenanceNoSource: 'Όπως εμφανίστηκε {at}',
    changedSince: 'Η εικόνα έχει αλλάξει από τότε.',
    observedHeading: 'Εικόνα όπως παρατηρήθηκε',
    imageCapturing: 'Φόρτωση εικόνας',
    imageMissing: 'Δεν υπάρχει εικόνα',
    imageUnavailable: 'Η εικόνα δεν είναι πλέον διαθέσιμη',
    imageRefused: 'Δεν ήταν δυνατή η χρήση της εικόνας',
    imageOpen: 'Άνοιγμα εικόνας: {alt}',
    imageInspect: 'Εικόνα · {alt}',
    imageSeen: 'προβλήθηκε {date}',
  },
  es: {
    captureLine: '{n} de {total} imágenes disponibles',
    provenance: '{source} · tal como se vio {at}',
    provenanceNoSource: 'Tal como se vio {at}',
    changedSince: 'La imagen ha cambiado desde entonces.',
    observedHeading: 'Imagen tal como se observó',
    imageCapturing: 'Cargando imagen',
    imageMissing: 'No hay ninguna imagen',
    imageUnavailable: 'La imagen ya no está disponible',
    imageRefused: 'No se pudo usar la imagen',
    imageOpen: 'Abrir imagen: {alt}',
    imageInspect: 'Imagen · {alt}',
    imageSeen: 'vista {date}',
  },
  et: {
    captureLine: 'Saadaval on {n} / {total} pilti',
    provenance: '{source} · vaadatud {at}',
    provenanceNoSource: 'Vaadatud {at}',
    changedSince: 'Pilt on sellest ajast muutunud.',
    observedHeading: 'Pilt vaadeldud kujul',
    imageCapturing: 'Pildi laadimine',
    imageMissing: 'Pilti pole',
    imageUnavailable: 'Pilt pole enam saadaval',
    imageRefused: 'Pilti ei saanud kasutada',
    imageOpen: 'Ava pilt: {alt}',
    imageInspect: 'Pilt · {alt}',
    imageSeen: 'vaadatud {date}',
  },
  fi: {
    captureLine: '{n}/{total} kuvaa saatavilla',
    provenance: '{source} · nähty {at}',
    provenanceNoSource: 'Nähty {at}',
    changedSince: 'Kuva on muuttunut sen jälkeen.',
    observedHeading: 'Havaittu kuva',
    imageCapturing: 'Ladataan kuvaa',
    imageMissing: 'Ei kuvaa',
    imageUnavailable: 'Kuva ei ole enää saatavilla',
    imageRefused: 'Kuvaa ei voitu käyttää',
    imageOpen: 'Avaa kuva: {alt}',
    imageInspect: 'Kuva · {alt}',
    imageSeen: 'nähty {date}',
  },
  fr: {
    captureLine: '{n} images sur {total} disponibles',
    provenance: '{source} · vue le {at}',
    provenanceNoSource: 'Vue le {at}',
    changedSince: 'L’image a changé depuis cette date.',
    observedHeading: 'Image telle qu’observée',
    imageCapturing: 'Chargement de l’image',
    imageMissing: 'Aucune image',
    imageUnavailable: 'L’image n’est plus disponible',
    imageRefused: 'Impossible d’utiliser l’image',
    imageOpen: 'Ouvrir l’image : {alt}',
    imageInspect: 'Image · {alt}',
    imageSeen: 'vue le {date}',
  },
  ga: {
    captureLine: '{n} as {total} íomhá ar fáil',
    provenance: '{source} · mar a chonacthas an {at}',
    provenanceNoSource: 'Mar a chonacthas an {at}',
    changedSince: 'Tá an íomhá athraithe ó shin.',
    observedHeading: 'Íomhá mar a breathnaíodh uirthi',
    imageCapturing: 'Íomhá á luchtú',
    imageMissing: 'Níl aon íomhá ann',
    imageUnavailable: 'Níl an íomhá ar fáil a thuilleadh',
    imageRefused: 'Níorbh fhéidir an íomhá a úsáid',
    imageOpen: 'Oscail an íomhá: {alt}',
    imageInspect: 'Íomhá · {alt}',
    imageSeen: 'feicthe an {date}',
  },
  hr: {
    captureLine: 'Dostupno slika: {n} od {total}',
    provenance: '{source} · viđeno {at}',
    provenanceNoSource: 'Viđeno {at}',
    changedSince: 'Slika se od tada promijenila.',
    observedHeading: 'Slika kako je zabilježena',
    imageCapturing: 'Učitavanje slike',
    imageMissing: 'Nema slike',
    imageUnavailable: 'Slika više nije dostupna',
    imageRefused: 'Slika se nije mogla upotrijebiti',
    imageOpen: 'Otvori sliku: {alt}',
    imageInspect: 'Slika · {alt}',
    imageSeen: 'viđeno {date}',
  },
  hu: {
    captureLine: '{n} / {total} kép érhető el',
    provenance: '{source} · megtekintve ekkor: {at}',
    provenanceNoSource: 'Megtekintve ekkor: {at}',
    changedSince: 'A kép azóta megváltozott.',
    observedHeading: 'A megfigyelt kép',
    imageCapturing: 'Kép betöltése',
    imageMissing: 'Nincs kép',
    imageUnavailable: 'A kép már nem érhető el',
    imageRefused: 'A kép nem használható',
    imageOpen: 'Kép megnyitása: {alt}',
    imageInspect: 'Kép · {alt}',
    imageSeen: 'megtekintve: {date}',
  },
  it: {
    captureLine: '{n} di {total} immagini disponibili',
    provenance: '{source} · come visualizzata il {at}',
    provenanceNoSource: 'Come visualizzata il {at}',
    changedSince: 'Da allora l’immagine è cambiata.',
    observedHeading: 'Immagine così come osservata',
    imageCapturing: 'Caricamento dell’immagine',
    imageMissing: 'Nessuna immagine',
    imageUnavailable: 'Immagine non più disponibile',
    imageRefused: 'Non è stato possibile usare l’immagine',
    imageOpen: 'Apri immagine: {alt}',
    imageInspect: 'Immagine · {alt}',
    imageSeen: 'visualizzata il {date}',
  },
  lt: {
    captureLine: 'Pasiekiami {n} iš {total} vaizdų',
    provenance: '{source} · matyta {at}',
    provenanceNoSource: 'Matyta {at}',
    changedSince: 'Nuo to laiko vaizdas pasikeitė.',
    observedHeading: 'Stebėtas vaizdas',
    imageCapturing: 'Įkeliamas vaizdas',
    imageMissing: 'Vaizdo nėra',
    imageUnavailable: 'Vaizdas nebepasiekiamas',
    imageRefused: 'Vaizdo nepavyko panaudoti',
    imageOpen: 'Atidaryti vaizdą: {alt}',
    imageInspect: 'Vaizdas · {alt}',
    imageSeen: 'matyta {date}',
  },
  lv: {
    captureLine: 'Pieejami {n} no {total} attēliem',
    provenance: '{source} · kā redzams {at}',
    provenanceNoSource: 'Kā redzams {at}',
    changedSince: 'Kopš tā laika attēls ir mainījies.',
    observedHeading: 'Novērotais attēls',
    imageCapturing: 'Notiek attēla ielāde',
    imageMissing: 'Nav attēla',
    imageUnavailable: 'Attēls vairs nav pieejams',
    imageRefused: 'Attēlu nevarēja izmantot',
    imageOpen: 'Atvērt attēlu: {alt}',
    imageInspect: 'Attēls · {alt}',
    imageSeen: 'skatīts {date}',
  },
  mt: {
    captureLine: '{n} minn {total} immaġni disponibbli',
    provenance: '{source} · kif dehret {at}',
    provenanceNoSource: 'Kif dehret {at}',
    changedSince: 'L-immaġni nbidlet minn dak iż-żmien.',
    observedHeading: 'Immaġni kif ġiet osservata',
    imageCapturing: 'Qed titgħabba l-immaġni',
    imageMissing: 'Ebda immaġni',
    imageUnavailable: 'L-immaġni m’għadhiex disponibbli',
    imageRefused: 'L-immaġni ma setgħetx tintuża',
    imageOpen: 'Iftaħ l-immaġni: {alt}',
    imageInspect: 'Immaġni · {alt}',
    imageSeen: 'dehret {date}',
  },
  nl: {
    captureLine: '{n} van {total} afbeeldingen beschikbaar',
    provenance: '{source} · gezien op {at}',
    provenanceNoSource: 'Gezien op {at}',
    changedSince: 'De afbeelding is sindsdien gewijzigd.',
    observedHeading: 'Afbeelding zoals waargenomen',
    imageCapturing: 'Afbeelding laden',
    imageMissing: 'Geen afbeelding',
    imageUnavailable: 'Afbeelding niet meer beschikbaar',
    imageRefused: 'Afbeelding kon niet worden gebruikt',
    imageOpen: 'Afbeelding openen: {alt}',
    imageInspect: 'Afbeelding · {alt}',
    imageSeen: 'gezien op {date}',
  },
  pl: {
    captureLine: '{n} z {total} dostępnych obrazów',
    provenance: '{source} · zaobserwowano {at}',
    provenanceNoSource: 'Zaobserwowano {at}',
    changedSince: 'Od tamtej pory obraz uległ zmianie.',
    observedHeading: 'Obraz w momencie obserwacji',
    imageCapturing: 'Ładowanie obrazu',
    imageMissing: 'Brak obrazu',
    imageUnavailable: 'Obraz nie jest już dostępny',
    imageRefused: 'Nie można użyć obrazu',
    imageOpen: 'Otwórz obraz: {alt}',
    imageInspect: 'Obraz · {alt}',
    imageSeen: 'wyświetlono {date}',
  },
  pt: {
    captureLine: '{n} de {total} imagens disponíveis',
    provenance: '{source} · visto em {at}',
    provenanceNoSource: 'Visto em {at}',
    changedSince: 'A imagem foi alterada desde então.',
    observedHeading: 'Imagem conforme observada',
    imageCapturing: 'Carregando imagem',
    imageMissing: 'Nenhuma imagem',
    imageUnavailable: 'A imagem não está mais disponível',
    imageRefused: 'Não foi possível usar a imagem',
    imageOpen: 'Abrir imagem: {alt}',
    imageInspect: 'Imagem · {alt}',
    imageSeen: 'vista em {date}',
  },
  ro: {
    captureLine: '{n} din {total} imagini disponibile',
    provenance: '{source} · observată la {at}',
    provenanceNoSource: 'Observată la {at}',
    changedSince: 'Imaginea s-a schimbat de atunci.',
    observedHeading: 'Imagine așa cum a fost observată',
    imageCapturing: 'Se încarcă imaginea',
    imageMissing: 'Nicio imagine',
    imageUnavailable: 'Imaginea nu mai este disponibilă',
    imageRefused: 'Imaginea nu a putut fi folosită',
    imageOpen: 'Deschide imaginea: {alt}',
    imageInspect: 'Imagine · {alt}',
    imageSeen: 'văzută la {date}',
  },
  sk: {
    captureLine: 'K dispozícii je {n} z {total} obrázkov',
    provenance: '{source} · zaznamenané {at}',
    provenanceNoSource: 'Zaznamenané {at}',
    changedSince: 'Obrázok sa odvtedy zmenil.',
    observedHeading: 'Zaznamenaný obrázok',
    imageCapturing: 'Načítava sa obrázok',
    imageMissing: 'Žiadny obrázok',
    imageUnavailable: 'Obrázok už nie je k dispozícii',
    imageRefused: 'Obrázok sa nepodarilo použiť',
    imageOpen: 'Otvoriť obrázok: {alt}',
    imageInspect: 'Obrázok · {alt}',
    imageSeen: 'zobrazené {date}',
  },
  sl: {
    captureLine: 'Na voljo je {n} od {total} slik',
    provenance: '{source} · videno {at}',
    provenanceNoSource: 'Videno {at}',
    changedSince: 'Slika se je od takrat spremenila.',
    observedHeading: 'Slika, kot je bila opažena',
    imageCapturing: 'Nalaganje slike',
    imageMissing: 'Ni slike',
    imageUnavailable: 'Slika ni več na voljo',
    imageRefused: 'Slike ni bilo mogoče uporabiti',
    imageOpen: 'Odpri sliko: {alt}',
    imageInspect: 'Slika · {alt}',
    imageSeen: 'videno {date}',
  },
  sv: {
    captureLine: '{n} av {total} bilder tillgängliga',
    provenance: '{source} · sedd {at}',
    provenanceNoSource: 'Sedd {at}',
    changedSince: 'Bilden har ändrats sedan dess.',
    observedHeading: 'Bilden som den sågs',
    imageCapturing: 'Laddar bild',
    imageMissing: 'Ingen bild',
    imageUnavailable: 'Bilden är inte längre tillgänglig',
    imageRefused: 'Bilden kunde inte användas',
    imageOpen: 'Öppna bilden: {alt}',
    imageInspect: 'Bild · {alt}',
    imageSeen: 'sedd {date}',
  },
};
export default appMediaCopy;
